/**
 * InkomendView — Incoming volume per day (pickup schedule)
 */

import { format, parseISO, isToday, isTomorrow } from 'date-fns';
import { nl } from 'date-fns/locale';
import clsx from 'clsx';
import type { InkomendDag } from '../../stores/wasserij-store';

interface InkomendViewProps {
  dagen: InkomendDag[];
  total7d: { orders: number; items: number; m2: number };
  isLoading: boolean;
}

function getDayLabel(dateStr: string): string {
  const date = parseISO(dateStr);
  if (isToday(date)) return 'Vandaag';
  if (isTomorrow(date)) return 'Morgen';
  return format(date, 'EEE d', { locale: nl });
}

export function InkomendView({ dagen, total7d, isLoading }: InkomendViewProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (dagen.length === 0) {
    return (
      <p className="text-muted text-center py-6">Geen ophaalafspraken gepland</p>
    );
  }

  return (
    <div>
      {/* Day cards */}
      <div className="flex gap-3 overflow-x-auto pb-2">
        {dagen.map(dag => {
          const date = parseISO(dag.datum);
          const isHighlight = isToday(date) || isTomorrow(date);

          return (
            <div
              key={dag.datum}
              className={clsx(
                'flex-shrink-0 w-[100px] rounded-xl border-2 p-3 text-center transition-all',
                isHighlight
                  ? 'bg-indigo-50 border-indigo-200'
                  : 'bg-white border-slate-200'
              )}
            >
              <p className={clsx(
                'text-xs font-medium mb-1',
                isHighlight ? 'text-indigo-600' : 'text-slate-500'
              )}>
                {getDayLabel(dag.datum)}
              </p>
              <p className={clsx(
                'text-2xl font-bold',
                isHighlight ? 'text-indigo-700' : 'text-slate-700'
              )}>
                {dag.aantalItems || dag.aantalOrders}
              </p>
              <p className="text-xs text-slate-400">
                {dag.totalM2 ? `${Math.round(dag.totalM2)} m²` : `${dag.aantalOrders} orders`}
              </p>
            </div>
          );
        })}
      </div>

      {/* 7-day total */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <p className="text-sm text-slate-500">
          Komende 7 dagen: <span className="font-semibold text-slate-700">{total7d.items || total7d.orders}</span> items
          {total7d.m2 > 0 && (
            <>
              {' '}&middot;{' '}
              <span className="font-semibold text-slate-700">{Math.round(total7d.m2)}</span> m²
            </>
          )}
        </p>
      </div>
    </div>
  );
}
